import { useParams, useSearchParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import useTheme from 'chat-ui/src/hooks/useTheme';
import { SearchParam } from '@common/ts';
import { IconLoader2 } from '@tabler/icons-react';
import useTranslation from '@/hooks/useTranslation';
import QuestionItem from '@/components/QuestionItem/QuestionItem';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import useReportStore from '@/store/ReportStore';
import FullPageLoader from '@/components/atoms/Loader/FullPageLoader';
import { cn } from '@/lib/utils';
import { useGetInterviewQuestions, useGetProjectQuestionInsights } from '@/reactQuery/get';
import { useCurrentProject } from '@/hooks/useCurrentProject';
import { ConversationState } from '@/enums';
import { InterviewQuestion } from '@/types';

interface QuestionsProps {
  isPendingReRun: boolean;
  analysisLanguage: string;
  newConversationsCount: number;
  onReRun: () => void;
  isRerunLoading: boolean;
  newConversationsState: ConversationState;
  isLoadingBeforeApplyFilter: boolean;
  setSelectedQuestionId: (questionId: string) => void;
  selectedQuestionId: string;
}

function Questions({
  isPendingReRun,
  analysisLanguage,
  newConversationsCount,
  onReRun,
  isRerunLoading,
  newConversationsState,
  isLoadingBeforeApplyFilter,
  setSelectedQuestionId,
  selectedQuestionId,
}: QuestionsProps) {
  const { lang } = useTranslation();
  const { project_id: projectId } = useParams();
  const [searchParams] = useSearchParams();
  const filterId = searchParams.get(SearchParam.FILTER_ID);
  const { currentProject: singleProjectData } = useCurrentProject();
  const { currentTheme } = useTheme();

  const [selectedQuestionText, setSelectedQuestionText] = useState<string>('');
  const setIsQuestionDataSelected = useReportStore((state) => state.setIsQuestionDataSelected);

  const { data: interviewQuestions, isPending: isPendingInterviewQuestions } =
    useGetInterviewQuestions(filterId, projectId);

  const {
    data: selectedQuestionInsights,
    isPending: isPendingInsights,
    error: errorInsights,
  } = useGetProjectQuestionInsights(filterId, projectId, selectedQuestionId);

  const onQuestionChange = (questionId: string) => {
    const selectedQuestion = interviewQuestions?.find((item) => item.id === questionId);
    if (!selectedQuestion) return;

    setSelectedQuestionId(questionId);
    setSelectedQuestionText(selectedQuestion?.question);
    setIsQuestionDataSelected(false);
  };

  useEffect(() => {
    setSelectedQuestionId(interviewQuestions?.[0]?.id ?? '');
    setSelectedQuestionText(interviewQuestions?.[0]?.question ?? '');
  }, [interviewQuestions]);

  if (errorInsights) return null;
  if (isPendingInsights || isLoadingBeforeApplyFilter) return <FullPageLoader />;

  return (
    <div className="w-full mx-auto">
      <Select value={selectedQuestionId} onValueChange={onQuestionChange}>
        <SelectTrigger
          style={{ borderColor: currentTheme?.primary }}
          className={cn(
            'flex h-fit w-full max-w-[856px] text-dark-text justify-between px-4 mb-2 font-semibold border-2 rounded-lg shadow-md border-primary',
            isPendingInterviewQuestions ? ' justify-center items-center' : 'self-start text-start',
          )}
        >
          {isPendingInterviewQuestions ? (
            <IconLoader2
              style={{ color: currentTheme?.primary }}
              size={18}
              className="mx-auto animate-spin"
            />
          ) : (
            <SelectValue placeholder="Select a question" />
          )}
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            <SelectLabel>
              {!isPendingInterviewQuestions ? (
                'Select a question'
              ) : (
                <IconLoader2
                  style={{ color: currentTheme?.primary }}
                  size={18}
                  className="mx-auto animate-spin"
                />
              )}
            </SelectLabel>
            {interviewQuestions?.map((item: InterviewQuestion, index: number) => (
              <SelectItem
                key={item?.id}
                value={item?.id}
                className="cursor-pointer flex items-center max-w-[845px]"
              >
                <div className="flex items-center gap-4">
                  <span className="whitespace-nowrap">
                    {lang.get('msg.question')} {index + 1}:
                  </span>
                  <span className="max-w-[700px] flex items-center py-0.5">{item.question}</span>
                </div>
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>

      <div className="mt-10 mb-4">
        <QuestionItem
          isPendingReRun={isPendingReRun}
          selectedQuestionInsights={selectedQuestionInsights}
          questionID={selectedQuestionId}
          questionText={selectedQuestionText}
          analysisLanguage={analysisLanguage}
          singleProjectData={singleProjectData}
          newConversationsCount={newConversationsCount}
          onReRun={onReRun}
          isRerunLoading={isRerunLoading}
          newConversationsState={newConversationsState}
          projectId={projectId}
          isPendingInterviewQuestions={isPendingInterviewQuestions}
        />
      </div>
    </div>
  );
}

export default Questions;
